import React, { useState, useEffect, forwardRef, useRef } from "react";
import styled from "styled-components";
import classNames from "classnames";
import IStyledProps from "../../utilities/IStyledProps";
import IEditableProps from "../../utilities/IEditableProps";
import {calculateDate} from "../../utilities/dateTime";
import { usePageProps } from '../../context/PagePropContext';
import BlogBreadcrumbs from "./BlogBreadcrumbs";
import Image from "../Image";
import { TbClockHour2 } from "react-icons/tb";
import BlogSocials from "./BlogSocials";

const BlogContainer = styled.div`
    .blog-heading {
        padding: 1.5rem;
        @media (min-width: 768px) {
            padding: 4rem 4rem 3rem 3rem;
        }
    }
    .gradient-background {
        @media (max-width: 768px) {
            background: transparent!important;
        }
    }
    .images-container {
        position: ralative;
        .blog-image {
            img {
                min-height: 480px;
                width: 100%;
                object-fit: cover;
            }
        }
        .float-image {
            position: absolute;
            left: 0;
            transform: translateY(-25%);
            z-index: 1;
            height: 150px;
            width: 150px;

            img {
                object-fit: cover;
            }

            @media (min-width: 1440px) {
                left: -5%;
            }
        }

        @media (min-width: 990px) {
            position: absolute;
        }
    }
    .blog-title {
        font-size: 1.5rem;
        font-family: "jubilat-variable", sans-serif;
        font-variation-settings: 'wght' 400;
        font-weight: 350;
        color: #5e514d;
        @media (min-width: 768px) {
            font-size: 2.222rem;
            color: #FFF;
        }
    }
    .blog-date {
        font-size: 0.875rem;
        margin-bottom: 0.611rem;
        color: #5e514d;
        @media (min-width: 768px) {
            color: #FFF;
        }
    }
    .quick-summary {
        padding: 1rem;
        font-size: 0.889rem;
        max-width: 100%;
        background: #E2E2E2;
        border-radius: .3rem;
        margin-top: 1rem;

        .quick-summary-title {
            font-weight: 700;
            margin-bottom: 0.5rem;
            font-family: 'National2 Narrow';
            text-transform: uppercase;
        } 
        .quick-summary-description {
            font-weight: 400;
            color: #FFF;
            font-style: italic;
        }

        @media (min-width: 990px) {
            padding: 35px 0 0 50px;
            max-width: 450px;
            background: transparent;
            border-radius: 0;
            margin-top: 0;
        }
    }
`;

const TagsContainer = styled.span`
    a {
        font-size: 0.688rem;
        font-weight: 300;
        color: #5e514d;
        @media (min-width: 768px) {
            color: #FFF;
        }
    }
    a:hover {
        text-decoration: none;
    }
`;

function Tags({ tags }) {
    return tags.map((tag, index) => (
        <TagsContainer className="pr-1 text-primary" key={tag}>
            <small>
                <a data-test-id="blog-tag-link" href={`/must-reads?tag=${tag}`} rel="noopener noreferrer" target="_blank">
                    {tag}{index !== tags.length - 1 && ","}
                </a>
            </small>
        </TagsContainer>
    ));
};

export interface IBlogHeaderProps extends IStyledProps, IEditableProps {
    dateType?: string;
    dateFormat?: string;
    src?: string;
    alt?: string;
    description?: string;
    headerClassName?: string;
    href?: string;
    title: string;
    timestamp: number | string;
    tags: string;
    testIdPrefix?: string;
    isMainArticle?: boolean;
    gradientClass?: string;
}

const BlogHeader = forwardRef((props: IBlogHeaderProps, ref: any) => {
    const { cmsId, editorClassObj, description, title, timestamp: blogPropsTimestamp, tags: tagsProp, src, alt, headerClassName, href, dateType = "date", dateFormat = "MMMM d, yyyy", testIdPrefix = '', isMainArticle=true, gradientClass } = props;
    const { timestamp: pagePropsTimestamp, updateTimestamp, displayUpdateTimestamp, tags, blogTitle, imageSrc, imageAlt } = usePageProps(); // these props come in separate from 'props' because they need to be in frontmatter for meta tags
    const displaytitle = title ?? blogTitle;
    const displayDate = blogPropsTimestamp ?? pagePropsTimestamp;
    const displayTags = tagsProp ?? tags;

    // Image sources
    const blogImageSrc = imageSrc;
    const blogImageAlt = imageAlt;
    const floatImageSrc = src;
    const floatImageAlt = alt;

    const [postAge, setPostAge] = useState(calculateDate(displayDate, dateType, dateFormat));
    const [postUpdateDate, setPostUpdateDate] = useState(calculateDate(updateTimestamp, dateType, dateFormat));
    const [url, setUrl] = useState(null);
    const [readTime, setReadTime] = useState(null);

    const dateRef = useRef(null);
    const mainRef = useRef(ref || null);

    useEffect(() => {
        if (window) {
            setUrl(window.location.href);
        }
    }, []);

    useEffect(() => {
        setPostAge(calculateDate(displayDate, dateType, dateFormat));
    }, [displayDate, dateType]);

    useEffect(() => {
        setPostUpdateDate(calculateDate(updateTimestamp || pagePropsTimestamp, dateType, dateFormat));
    }, [displayUpdateTimestamp, updateTimestamp, dateType]);

    useEffect(() => {
        if (dateRef.current && mainRef.current) {
            const previousElement = mainRef.current.previousElementSibling;
            if (!previousElement || !(previousElement.classList.contains('yt-thumbnail-container') || previousElement.classList.contains('yt-video-container'))) {
                return;
            };

            const parentElement = previousElement.parentElement;
            if (parentElement && dateRef.current && !document.body.contains(dateRef.current)) {
                dateRef.current.setAttribute('style', 'margin-bottom: 0.611rem;');
                parentElement.insertBefore(dateRef.current, previousElement);
            }
        }

        return () => {
            if (dateRef.current && dateRef.current.parentElement) {
                dateRef.current.parentElement.removeChild(dateRef.current);
            }
        };
    }, [url, dateRef, mainRef]);

    useEffect(() => { // Calculate read time
        let element = document.querySelector('.custom-content.markdown-container');

        if (!element) {
            element = document.querySelector('.custom-content.markup-container');
        }

        if (element) {
            const readWordsMin = 175;
            const readWordsMax = 250;
            const blogWordCount = element.textContent.trim().split(/\s+/).length;
            const readTimeMin = Math.round(blogWordCount / readWordsMax);
            const readTimeMax = Math.round(blogWordCount / readWordsMin);

            setReadTime(readTimeMin === readTimeMax ? readTimeMin.toString() : `${readTimeMin}-${readTimeMax}`);
        }
    }, [url]);

    return (
        <div ref={mainRef} data-id={cmsId} className={classNames(editorClassObj, 'mb-4')}>
            <BlogContainer>
                <div className={classNames("content border-0 gradient-background", gradientClass)}>
                    <div className={classNames("container content border-0")}>
                        <div className="row no-gutters d-flex justify-content-between">
                            <div className="col">
                                <BlogBreadcrumbs />
                            </div>
                            <div className="col-auto py-4 px-xl-6 px-lg-5 px-4 d-none d-md-flex align-items-end ">
                                <BlogSocials url={url} size={25}/>
                            </div>
                        </div>
                        <div className="row no-gutters">
                            <div className="col-12 col-md-7">
                                <div className="images-container">
                                    <Image dataTestId="blog-image" src={blogImageSrc} alt={blogImageAlt} className="blog-image"/>
                                    <Image dataTestId={`${testIdPrefix}float-image`} src={floatImageSrc} alt={floatImageAlt} className="float-image d-none d-lg-block" />
                                </div>
                            </div>
                            <div className="col-12 col-md-5 blog-heading">
                                <div className="row no-gutters">
                                    <div className="col-12">
                                        <h1 className={classNames("blog-title", headerClassName)} data-testid="blog-title">
                                            {displaytitle}
                                        </h1>
                                    </div>
                                    <div className="col-auto col-md-12 p-0 d-inline-block order-2 order-md-1 text-right text-md-left">
                                        <div className="mt-2 blog-tags">
                                            <Tags tags={displayTags ?
                                                    displayTags.split(",")
                                                        .map(tag => tag.trim())
                                                        .filter(tag => tag !== "")
                                                    : []} />
                                        </div>
                                    </div>
                                    <div className="col col-md-12 p-0 mt-3 d-inline-block order-1 order-md-2 text-muted">
                                        <div data-test-id="blog-date" className="blog-date d-flex align-items-start align-items-md-center flex-column flex-md-row" ref={dateRef}>
                                            {isMainArticle && readTime && <span>
                                                <TbClockHour2 size={24} /> {readTime} minute read<span className="d-none d-md-inline">&nbsp;|&nbsp;</span>
                                            </span>}
                                            {postUpdateDate && displayUpdateTimestamp && <span>
                                                Updated {dateType === 'timeAgo' ? postUpdateDate : postUpdateDate}<span className="d-none d-md-inline">&nbsp;|&nbsp;</span>
                                            </span>}
                                            <span>
                                                Published {dateType === 'timeAgo' ? postAge : postAge}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="content container border-0">
                    <div className="row">
                        <div className="col-12 offset-lg-7 col-lg-5 quick-summary">
                            <p className="quick-summary-title">Quick Summary</p>
                            <p className="quick-summary-description text-muted">{description}</p>
                        </div>
                    </div>
                </div>
            </BlogContainer>
            
        </div>
    );
});

export default BlogHeader;